import type * as t from "./types";

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import { Sequence, type SequenceNode } from "./sequence";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function getRelativeStepIdx(
    segments: Sequence<t.Segment>,
    globalStepIdx: number,
) {
    if (globalStepIdx === -1) {
        return -1;
    }

    let idx = 0;
    let globalIdx = 0;

    for (const seg of segments.iterate()) {
        for (const _ of seg.value.steps.iterate()) {
            if (globalIdx === globalStepIdx) {
                return idx;
            }
            ++idx;
            ++globalIdx;
        }
        idx = 0;
    }

    throw new Error(
        "[sphln-quiz]: Unable to get relative step index from global index",
    );
}

function getDiscountPrizeText(prize: t.DiscountPrize) {
    if (prize.type !== "discount") {
        throw new Error("prize is not a discount");
    }

    let prizeText;
    switch (prize.discount_type) {
        case "percentage":
            prizeText = `${prize.discount_value}% off`;
            break;
        case "fixed":
            prizeText = `$${prize.discount_value.toFixed(2)} off`;
            break;
        default:
            throw new Error(
                `Invalid discount prize type ${prize.discount_type}`,
            );
    }

    return prizeText;
}

function getCustomPizeText(prize: t.CustomPrize) {
    return prize.custom_value;
}

export function getPrizeText(prize: t.SegmentConfig["prize"]) {
    switch (prize.type) {
        case "free_shipping":
            return "free shipping";
        case "discount":
            return getDiscountPrizeText(prize);
        case "custom":
            return getCustomPizeText(prize);
        default:
            throw new Error(`Invalid prize type ${(prize as any)?.type}`);
    }
}

export function capitalizeFirstLetter(str: string) {
    const result = [];

    let isFirstCharInWord = true;
    for (const char of str) {
        let charCpy;
        if (isFirstCharInWord && char !== " ") {
            charCpy = char.toUpperCase();
        } else {
            charCpy = char;
        }

        result.push(charCpy);

        isFirstCharInWord = char === " ";
    }

    return result.join("");
}

export function getSegmentConfig(
    config: t.QuizConfig,
    segmentNode: SequenceNode<t.Segment> | undefined,
) {
    if (segmentNode == null) {
        return;
    }
    return config.segments.find(
        (segment) => segment.id === segmentNode.value.id,
    );
}

export function createSegments(config: t.QuizConfig, steps: t.Step[]) {
    const segments: t.Segment[] = [];
    let segmentIdx = 0;
    let currSteps: t.Step[] = [];
    for (const step of steps) {
        currSteps.push(step);

        if (step.type === "segment_result") {
            // We need to rely on some kind of segment id because the first
            // segment is always a fixed segment. If we relied solely on the
            // position of the segment in the sequence, we would have to always
            // be adjusting the index (idx+1) to compensate for the first, fixed
            // segment that has no config. I chose to just copy the id that
            // comes with the config.
            const segmentId = config.segments[segmentIdx]?.id;
            segments.push({
                id: segmentId,
                steps: new Sequence(currSteps),
            });
            currSteps = [];
            ++segmentIdx;
        }
    }

    return segments;
}

export function displayConfigError(msg: string) {
    if (!window.Shopify?.designMode) {
        return;
    }
    alert(msg);
}

export function hexToRgb(hex: string) {
    hex = hex.replace(/^#/, "");

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
}

export function camelCaseToCssVariable(camelCaseString: string) {
    return (
        "--" +
        camelCaseString.replace(
            /([A-Z])/g,
            (match) => "-" + match.toLowerCase(),
        )
    );
}

/**
 * Finds and returns the data set by the host that instantiates the app.
 * */
export function getHostData(instanceId: string) {
    const shpln_quiz = window.shpln_quiz
        ? window.shpln_quiz[instanceId]
        : undefined;
    if (shpln_quiz == null) {
        throw new Error("[shpln-quiz] Instance data cannot be found.");
    }
    return shpln_quiz;
}

export function isNumeric(str: string) {
    return /^\d+$/.test(str);
}

export function getShopifyDiscountCodeUrl(
    discountCode: string,
    redirectUrl: string,
): string {
    return `/discount/${discountCode}?redirect=${redirectUrl}`;
}
